// Fonte unica de verdade para os limites de midia do Instagram Messaging API.
// Valores confirmados na documentacao da Meta (Send a Message / Instagram Messaging):
// https://developers.facebook.com/docs/messenger-platform/instagram/features/send-message/
// https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/messaging-api/
//
// image: PNG/JPEG ate 8MB | video: MP4/OGG/AVI/MOV/WEBM ate 25MB
// audio: AAC/M4A/WAV/MP4 ate 25MB | file: PDF ate 25MB (unico documento suportado)
//
// Audio e tratado a parte (matchPrefix em vez de mimes exatos): o backend
// reencodifica QUALQUER audio/* pra M4A via ffmpeg antes do upload (uploadToS3 +
// convertAudioToFile), entao o formato de origem nao importa aqui — inclusive o
// microfone do navegador grava webm/opus por padrao, que nao esta na lista da
// Meta mas chega la convertido. Image/video/file NAO tem essa conversao no
// backend, entao para eles o mimetype precisa bater exatamente com o suportado.

export const INSTAGRAM_MEDIA_RULES = {
    image: {
        mimes: ['image/png', 'image/jpeg', 'image/gif'],
        maxBytes: 8 * 1024 * 1024,
        label: 'PNG, JPEG',
    },
    video: {
        mimes: ['video/mp4', 'video/ogg', 'video/avi', 'video/x-msvideo', 'video/quicktime', 'video/webm'],
        maxBytes: 25 * 1024 * 1024,
        label: 'MP4, OGG, AVI, MOV, WEBM',
    },
    audio: {
        matchPrefix: 'audio/',
        maxBytes: 25 * 1024 * 1024,
        label: 'AAC, M4A, WAV (qualquer áudio é convertido automaticamente)',
    },
    file: {
        mimes: ['application/pdf'],
        maxBytes: 25 * 1024 * 1024,
        label: 'PDF',
    },
};

// react-dropzone `accept` prop: mimes aceitos no canal Instagram (audio via wildcard).
export const INSTAGRAM_ACCEPT = [
    ...INSTAGRAM_MEDIA_RULES.image.mimes,
    ...INSTAGRAM_MEDIA_RULES.video.mimes,
    'audio/*',
    ...INSTAGRAM_MEDIA_RULES.file.mimes,
];

// Rotulos legiveis para a UI (dialog de limites, mensagens de erro).
export const INSTAGRAM_LIMIT_LABELS = {
    image: { size: '8 MB', formats: INSTAGRAM_MEDIA_RULES.image.label },
    video: { size: '25 MB', formats: INSTAGRAM_MEDIA_RULES.video.label },
    audio: { size: '25 MB', formats: INSTAGRAM_MEDIA_RULES.audio.label },
    file: { size: '25 MB', formats: INSTAGRAM_MEDIA_RULES.file.label },
};

// Determina o "kind" (image/video/audio/file) de um File a partir do mimetype.
// Retorna null quando o formato nao e suportado pelo Instagram Messaging.
export function getInstagramMediaKind(file) {
    const mime = file?.type || '';
    if (mime.startsWith(INSTAGRAM_MEDIA_RULES.audio.matchPrefix)) return 'audio';
    for (const [kind, rule] of Object.entries(INSTAGRAM_MEDIA_RULES)) {
        if (kind === 'audio') continue;
        if (rule.mimes.includes(mime)) return kind;
    }
    return null;
}

// Valida um File contra as regras do Instagram. Retorna:
//   { ok: true, kind }
//   { ok: false, reason: 'format', kind: null }
//   { ok: false, reason: 'size', kind, maxBytes }
export function validateInstagramFile(file) {
    const kind = getInstagramMediaKind(file);
    if (!kind) return { ok: false, reason: 'format', kind: null };
    const rule = INSTAGRAM_MEDIA_RULES[kind];
    if (file.size > rule.maxBytes) return { ok: false, reason: 'size', kind, maxBytes: rule.maxBytes };
    return { ok: true, kind };
}

// Particiona uma lista de Files em aceitos/rejeitados (com o motivo de cada rejeicao).
export function partitionInstagramFiles(files) {
    const aceitos = [];
    const rejeitados = [];
    for (const file of files || []) {
        const result = validateInstagramFile(file);
        if (result.ok) {
            aceitos.push(file);
        } else {
            rejeitados.push({ file, ...result });
        }
    }
    return { aceitos, rejeitados };
}
